import { useState } from 'react';
import { Link } from 'react-router';
import { useApi } from '../lib/api';
import { money, num, pct, period } from '../lib/format';
import { useSession } from '../lib/session';
import type { AttentionItem, Checklist, CommandCenter } from '../lib/types';
import { BarChart, Legend, Sparkline } from '../components/charts';
import { Icons } from '../components/icons';
import { Badge, ButtonLink, Card, cx, Empty, Loading, PageHeader, Progress, Query, Stat } from '../components/ui';
import { PinnedDashboard } from './Dashboards';

const KIND: Record<AttentionItem['kind'], { label: string; icon: React.ReactNode; tone: string }> = {
  APPROVAL: { label: 'Approval', icon: <Icons.Inbox size={15} />, tone: 'text-warn bg-warn-soft' },
  UNMATCHED_CASH: { label: 'Cash', icon: <Icons.Bank size={15} />, tone: 'text-info bg-info-soft' },
  OVERDUE_INVOICE: { label: 'Collections', icon: <Icons.Receipt size={15} />, tone: 'text-bad bg-bad-soft' },
  BILL_DUE: { label: 'Payables', icon: <Icons.Bill size={15} />, tone: 'text-ink-2 bg-sunken' },
  ACCRUAL: { label: 'Accrual', icon: <Icons.Clock size={15} />, tone: 'text-brand bg-brand-soft' },
  ANOMALY: { label: 'Anomaly', icon: <Icons.Alert size={15} />, tone: 'text-bad bg-bad-soft' },
  CONTROL: { label: 'Control', icon: <Icons.Shield size={15} />, tone: 'text-warn bg-warn-soft' },
};

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

export function CommandCenterPage() {
  const { me, currency, can } = useSession();
  const q = useApi<CommandCenter>('/api/command-center', { refetchInterval: 60_000 });
  const next = q.data?.close.nextToClose;
  const checklist = useApi<Checklist>(next ? `/api/close/${next}/checklist` : null);

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${me.user.name.split(' ')[0]}`}
        description={q.data ? `Here is where the books stand as of ${q.data.asOf}. Everything below updates as transactions post.` : 'Loading the latest position…'}
        actions={
          can('ACCOUNTANT') && (
            <>
              <ButtonLink to="/journal/new" icon={<Icons.Plus size={15} />}>
                Journal entry
              </ButtonLink>
              <ButtonLink to="/close" variant="primary" icon={<Icons.CheckCircle size={15} />}>
                Run the close
              </ButtonLink>
            </>
          )
        }
      />
      <Query q={q} loading={<Loading label="Computing your position…" />}>
        {(d) => {
          const k = d.kpis;
          const cashTrend = d.trends.cash.map((x) => x.cash);
          const mrrTrend = d.trends.mrr.map((x) => x.mrr);
          const mrrPrev = mrrTrend.length > 1 ? mrrTrend[mrrTrend.length - 2]! : null;
          const mrrDelta = mrrPrev ? (k.mrr - mrrPrev) / mrrPrev : null;
          const high = d.attention.filter((a) => a.severity === 'HIGH').length;
          // A company with no cash movements yet has no burn to measure: say so rather than an infinite runway.
          const noBurn = k.monthlyBurn === 0;
          return (
            <div className="space-y-5">
              {can('ACCOUNTANT') && <GettingStarted setup={d.setup} orgId={me.orgId} />}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <Stat
                  label="Cash"
                  value={money(k.cash, currency, { compact: true })}
                  sub={noBurn ? 'No net burn yet' : k.monthlyBurn < 0 ? `${money(-k.monthlyBurn, currency, { compact: true })}/mo net inflow` : `${money(k.monthlyBurn, currency, { compact: true })}/mo net burn`}
                >
                  <Sparkline values={cashTrend} className="mt-2" />
                </Stat>
                <Stat
                  label="Runway"
                  value={k.runwayMonths !== null ? `${num(k.runwayMonths, 1)} mo` : noBurn ? '—' : '∞'}
                  sub={k.runwayMonths !== null ? 'At the trailing burn rate' : noBurn ? 'Needs a month of spending' : 'Cash-flow positive'}
                  tone={k.runwayMonths !== null && k.runwayMonths < 12 ? 'bad' : k.runwayMonths === null && !noBurn ? 'good' : undefined}
                />
                <Stat label="MRR" value={money(k.mrr, currency, { compact: true })} sub={mrrDelta === null ? `ARR ${money(k.arr, currency, { compact: true })}` : `${mrrDelta >= 0 ? '▲' : '▼'} ${pct(Math.abs(mrrDelta))} MoM · ARR ${money(k.arr, currency, { compact: true })}`} tone={mrrDelta !== null && mrrDelta < 0 ? 'bad' : 'good'}>
                  <Sparkline values={mrrTrend} className="mt-2" color="var(--color-series-2)" />
                </Stat>
                <Stat
                  label="Net revenue retention"
                  value={pct(k.nrr, 0)}
                  sub={k.customers === 0 ? 'No active contracts yet' : `${k.customers} active ${k.customers === 1 ? 'customer' : 'customers'}`}
                  tone={k.nrr === null ? undefined : k.nrr >= 1 ? 'good' : 'warn'}
                />
                <Stat label="Receivables" value={money(k.arOpen, currency, { compact: true })} sub={k.arOverdue ? `${money(k.arOverdue, currency, { compact: true })} overdue` : 'Nothing overdue'} tone={k.arOverdue ? 'bad' : 'good'} />
                <Stat label="Payables" value={money(k.apOpen, currency, { compact: true })} sub="Open approved bills" />
              </div>

              <div className="grid gap-5 xl:grid-cols-3">
                <Card
                  className="xl:col-span-2"
                  title="Revenue and net income"
                  subtitle="Recognized revenue by month (ASC 606), straight from the ledger"
                  actions={
                    <Link to="/reports/income-statement" className="text-[12.5px] font-medium text-brand hover:underline">
                      Income statement →
                    </Link>
                  }
                >
                  <BarChart
                    labels={d.trends.revenue.map((r) => period(r.period, true))}
                    series={[
                      { name: 'Revenue', values: d.trends.revenue.map((r) => r.revenue), color: 'var(--color-series-1)' },
                      { name: 'Net income', values: d.trends.revenue.map((r) => r.netIncome), color: 'var(--color-series-3)' },
                    ]}
                    height={230}
                    format={(v) => money(v, currency)}
                    axisFormat={(v) => money(v, currency, { compact: true })}
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <Legend series={[{ name: 'Revenue', color: 'var(--color-series-1)' }, { name: 'Net income', color: 'var(--color-series-3)' }]} />
                    <span className="text-[12px] text-muted">
                      Last month: <span className="num text-ink">{money(k.revenueLastMonth, currency)}</span> revenue, <span className={cx('num', k.netIncomeLastMonth < 0 ? 'text-bad' : 'text-good')}>{money(k.netIncomeLastMonth, currency)}</span> net
                    </span>
                  </div>
                </Card>

                <div className="space-y-5">
                  <Card
                    title="Close"
                    actions={
                      <Link to="/close" className="text-[12.5px] font-medium text-brand hover:underline">
                        Open →
                      </Link>
                    }
                  >
                    <div className="grid grid-cols-3 gap-3 text-[12px]">
                      <div>
                        <div className="text-muted">Last closed</div>
                        <div className="mt-0.5 font-medium">{period(d.close.lastClosed)}</div>
                      </div>
                      <div>
                        <div className="text-muted">Closing now</div>
                        <div className="mt-0.5 font-medium">{period(d.close.nextToClose)}</div>
                      </div>
                      <div>
                        <div className="text-muted">Live period</div>
                        <div className="mt-0.5 font-medium">{period(d.close.currentPeriod)}</div>
                      </div>
                    </div>
                    {checklist.data && (
                      <div className="mt-4">
                        <div className="mb-1.5 flex justify-between text-[12px]">
                          <span className="text-muted">{period(checklist.data.period)} checklist</span>
                          <span className="num font-medium">
                            {checklist.data.done}/{checklist.data.total}
                          </span>
                        </div>
                        <Progress value={checklist.data.progress} tone={checklist.data.progress === 1 ? 'good' : 'brand'} />
                      </div>
                    )}
                  </Card>
                  <Card
                    title="Continuous controls"
                    actions={
                      <Link to="/controls" className="text-[12.5px] font-medium text-brand hover:underline">
                        Details →
                      </Link>
                    }
                  >
                    <div className="flex items-center gap-4">
                      <div className={cx('flex size-11 items-center justify-center rounded-full', d.controls.FAIL ? 'bg-bad-soft text-bad' : d.controls.WARN ? 'bg-warn-soft text-warn' : 'bg-good-soft text-good')}>
                        <Icons.Shield size={22} />
                      </div>
                      <div className="text-[12.5px]">
                        <div className="font-medium text-ink">{d.controls.FAIL ? `${d.controls.FAIL} control(s) failing` : d.controls.WARN ? 'Books tie out, with warnings' : 'Every control passes'}</div>
                        <div className="mt-0.5 flex gap-3 text-muted">
                          <span>
                            <span className="num font-medium text-good">{d.controls.PASS}</span> pass
                          </span>
                          <span>
                            <span className="num font-medium text-warn">{d.controls.WARN}</span> warn
                          </span>
                          <span>
                            <span className="num font-medium text-bad">{d.controls.FAIL}</span> fail
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>

              <Card title="Needs attention" subtitle={d.attention.length ? `${d.attention.length} items · ${high} high priority` : undefined} pad={false}>
                {d.attention.length === 0 ? (
                  <Empty title="Nothing needs you right now" icon={<Icons.CheckCircle size={28} />}>
                    Approvals, cash, collections, accruals, anomalies and controls are all clear.
                  </Empty>
                ) : (
                  <ul className="divide-y divide-ink/[0.06]">
                    {d.attention.map((a) => {
                      const meta = KIND[a.kind];
                      return (
                        <li key={a.id}>
                          <Link to={a.link} className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-white/60">
                            <span className={cx('flex size-8 shrink-0 items-center justify-center rounded-[10px]', meta.tone)}>{meta.icon}</span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-[13px] font-medium text-ink">{a.title}</span>
                                {a.severity === 'HIGH' && <Badge tone="bad">High</Badge>}
                              </div>
                              <div className="truncate text-[12.5px] text-muted">{a.detail}</div>
                            </div>
                            <span className="hidden text-[11.5px] text-faint sm:block">{meta.label}</span>
                            {a.amount !== undefined && <span className="num w-28 text-right text-[13px] text-ink">{money(a.amount, a.currency ?? currency)}</span>}
                            <span className="text-faint group-hover:text-brand">
                              <Icons.Chevron size={15} />
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>

              <PinnedDashboard />
            </div>
          );
        }}
      </Query>
    </>
  );
}

type Setup = CommandCenter['setup'];

/** First-run steps for a new company, until they are all done or someone hides the card. */
function GettingStarted({ setup, orgId }: { setup: Setup; orgId: string }) {
  const key = `raremargin:setup-hidden:${orgId}`;
  const [hidden, setHidden] = useState(() => localStorage.getItem(key) === '1');
  const steps = [
    { done: setup.bankAccounts > 0, title: 'Add your bank account', text: 'Tie a bank account to its cash account in the ledger.', to: '/cash?tab=accounts&add=1' },
    { done: setup.bankTransactions > 0, title: 'Import a bank statement', text: 'Upload a CSV; every line gets a suggested match.', to: '/cash?tab=accounts' },
    { done: setup.products > 0, title: 'Add what you sell', text: 'Products say how their revenue is recognized.', to: '/ar/products' },
    { done: setup.contracts + setup.invoices > 0, title: 'Bill a customer', text: 'A contract schedules invoices and revenue for you.', to: '/ar/contracts/new' },
    { done: setup.bills > 0, title: 'Enter a vendor bill', text: 'Approved bills post to payables and expense.', to: '/ap/bills/new' },
    { done: setup.members + setup.invites > 1, title: 'Invite your team', text: 'Someone else approves what you submit.', to: '/settings?tab=members' },
  ];
  const done = steps.filter((x) => x.done).length;
  if (hidden || done === steps.length) return null;
  const hide = () => {
    localStorage.setItem(key, '1');
    setHidden(true);
  };
  return (
    <Card
      title="Set up your books"
      subtitle={`${done} of ${steps.length} done. Each step takes a minute or two.`}
      actions={
        <button onClick={hide} className="rounded-lg px-2 py-1 text-[12.5px] text-muted hover:bg-ink/[0.05] hover:text-ink">
          Hide
        </button>
      }
    >
      <Progress value={done / steps.length} className="mb-4" />
      <ol className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {steps.map((x, i) => (
          <li key={x.title}>
            <Link
              to={x.to}
              className={cx('group flex h-full items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors', x.done ? 'border-transparent bg-good-soft/50' : 'border-white/80 bg-white/70 hover:border-brand-line hover:bg-white')}
            >
              <span className={cx('mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold', x.done ? 'bg-good text-white' : 'bg-brand-soft text-brand')}>
                {x.done ? <Icons.Check size={12} /> : i + 1}
              </span>
              <span className="min-w-0">
                <span className={cx('block text-[13px] font-medium', x.done ? 'text-muted line-through decoration-ink/20' : 'text-ink')}>{x.title}</span>
                <span className="block text-[12px] text-muted">{x.text}</span>
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </Card>
  );
}
